import * as THREE from 'three';
import { bronze, canDraw } from './hud-materials.js';
import { boltGeometry, copiesGeometry, iron, mergedGeometry, octagonPoint, octagonVertex, polygonFrameGeometry, polygonGeometry } from './hud-parts.js';
import { ButtonKit, OctagonButton } from './octagon-button.js';
import { unpickable } from './radial-menu.js';
import { hudIcons } from '../../presentation/icons.js';

/** The chart face's apothem, in CSS pixels at size 1. */
export const CHART_FACE = 96;
const RIM = CHART_FACE + 12;
/** How far the compass rose's long points reach from the centre: well past the rim. */
export const CHART_REACH = CHART_FACE + 62;
/** North on screen: the camera looks north-west, so north is up and to the right. */
const NORTH = Math.PI / 4;

/** What the chart shows: its drawing (square, turned to the screen), whether it is unfolded, and the place name under it. */
export interface ChartState { canvas: HTMLCanvasElement; revision: number; expanded: boolean; location: string; focus: { x: number; z: number } }

/**
 * The map chart, set in a faceted bronze octagon like the medallions, over a compass rose whose eight points show past the rim.
 * The rose is a floating card: north always settles toward the upper right, where north lies with this camera, but it swings and
 * trembles as the view pans and settles again, like a real compass. Two buttons sit on the lower corners (find the party,
 * unfold the chart) and a bronze plaque under the face names the nearest place. Units are CSS pixels; the HUD places and scales it.
 */
export class HudChart extends THREE.Group {
  readonly follow: OctagonButton;
  readonly expand: OctagonButton;
  private kit: ButtonKit;
  private face: THREE.Mesh;
  private faceMaterial = new THREE.MeshStandardMaterial({ color: 0xeee2c8, roughness: .7, metalness: 0, emissive: 0xffffff, emissiveIntensity: .32 });
  /**
   * The drawing as uploaded: its bytes as they are, no colour conversion (uploading a canvas this size straight into an sRGB texture
   * cost the GPU about 12 ms, ten times a second), and the sRGB, mipmapped copy the face shows, which `draw` fills on the GPU.
   */
  private raw: THREE.CanvasTexture | null = null;
  private target: THREE.WebGLRenderTarget | null = null;
  private decode = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), decodeMaterial());
  private decodeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private dirty = false;
  private drawn = -1;
  private facets = Object.assign(bronze(61, 0x5a452c), { flatShading: true });
  private metal = bronze(67, 0x4f3d28);
  private brass = new THREE.MeshStandardMaterial({ color: 0x8c6a42, metalness: .55, roughness: .4, flatShading: true });
  private shade = new THREE.MeshStandardMaterial({ color: 0x3e2c1a, metalness: .5, roughness: .5, flatShading: true });
  private gilt = new THREE.MeshStandardMaterial({ color: 0xf0cc80, metalness: .6, roughness: .28, emissive: 0x8a5e1c, emissiveIntensity: .8, flatShading: true });
  private giltShade = new THREE.MeshStandardMaterial({ color: 0xb08240, metalness: .6, roughness: .35, emissive: 0x4a3010, emissiveIntensity: .6, flatShading: true });
  private hardware = iron();
  private card = new THREE.Group();
  /** The card's swing away from north (radians) and how fast it is turning. */
  private swing = 0; private spin = 0;
  private lastFocus: { x: number; z: number } | null = null;
  private plaque: THREE.Mesh;
  private label: THREE.Mesh;
  private labelCanvas = canDraw() ? Object.assign(document.createElement('canvas'), { width: 512, height: 64 }) : null;
  private labelTexture = this.labelCanvas ? new THREE.CanvasTexture(this.labelCanvas) : null;
  private labelText = '';
  private disposables: { dispose(): void }[] = [];
  private time = 0;
  hovered = false;

  private ownsKit: boolean;

  /** `kit` shares a layer's button parts and glyph faces; without it the chart makes and disposes its own. */
  constructor(glow: THREE.Texture | null, kit?: ButtonKit) {
    super();
    const keep = <T extends { dispose(): void }>(item: T) => { this.disposables.push(item); return item; };
    this.kit = kit ?? new ButtonKit(glow); this.ownsKit = !kit;
    // The face, its rim and a backing plate, the same make as a medallion.
    this.face = new THREE.Mesh(keep(polygonGeometry(8, octagonVertex(CHART_FACE))), this.faceMaterial); this.face.position.z = -2;
    this.face.userData.chart = 'face';
    const rim = new THREE.Mesh(keep(polygonFrameGeometry(8, octagonVertex(RIM), octagonVertex(CHART_FACE - 1), 5, 1.8, 1)), this.facets); rim.position.z = -3;
    const hairline = new THREE.Mesh(keep(polygonFrameGeometry(8, octagonVertex(CHART_FACE + 1.5), octagonVertex(CHART_FACE - .5), .6, .3, 1)), this.gilt); hairline.position.z = 2.6;
    const backing = new THREE.Mesh(keep(polygonFrameGeometry(8, octagonVertex(RIM + 3), 0, 4, 2, 1)), this.metal); backing.position.z = -12;
    this.add(backing, rim, hairline, this.face);
    this.add(new THREE.Mesh(keep(copiesGeometry(boltGeometry(3.4), [45, 135].map(angle => octagonPoint(RIM - 5, angle * Math.PI / 180, 5)))), this.hardware));
    if (glow) {
      const shadow = unpickable(new THREE.Sprite(keep(new THREE.SpriteMaterial({ map: glow, color: 0x000000, transparent: true, opacity: .7, depthWrite: false }))));
      shadow.scale.setScalar(CHART_REACH * 3); shadow.position.set(8, -12, -60); this.add(shadow);
    }
    this.buildRose(keep);
    // The plaque under the face, with the place name cut into it.
    this.plaque = new THREE.Mesh(keep(plaqueGeometry(168, 28)), this.facets); this.plaque.position.set(0, -RIM - 4, 3);
    const labelMaterial = keep(new THREE.MeshBasicMaterial({ map: this.labelTexture, transparent: true, depthWrite: false }));
    this.label = unpickable(new THREE.Mesh(keep(new THREE.PlaneGeometry(160, 20)), labelMaterial)); this.label.position.set(0, -RIM - 4, 10.5);
    if (this.labelTexture) this.labelTexture.colorSpace = THREE.SRGBColorSpace; else this.label.visible = false;
    this.add(this.plaque, this.label);
    // Find the party at the lower left corner, unfold the chart at the lower right.
    this.follow = new OctagonButton('chart:follow', hudIcons.focus, 0xd8c08a, this.kit);
    this.expand = new OctagonButton('chart:expand', hudIcons.expand, 0xd8c08a, this.kit);
    this.follow.position.copy(octagonPoint(RIM + 12, Math.PI * 1.125, 4)); this.expand.position.copy(octagonPoint(RIM + 12, Math.PI * 1.875, 4));
    this.add(this.follow, this.expand);
    keep(this.faceMaterial); keep(this.facets); keep(this.metal); keep(this.brass); keep(this.shade); keep(this.gilt); keep(this.giltShade); keep(this.hardware);
  }

  /**
   * The compass rose behind the face: four long points on the cardinals and four short ones between, each a two-faced blade lit
   * on one flank and shaded on the other, round a brass ring. North's blade is longer and gilt. The card turns as one piece, so each
   * material's parts are one geometry.
   */
  private buildRose(keep: <T extends { dispose(): void }>(item: T) => T) {
    const blade = (length: number, width: number, side: 1 | -1) => {
      const shape = new THREE.Shape([new THREE.Vector2(0, 0), new THREE.Vector2(length, 0), new THREE.Vector2(0, width * side)]);
      return new THREE.ExtrudeGeometry(shape, { depth: 2, bevelEnabled: true, bevelSize: .6, bevelThickness: .8, bevelSegments: 1 });
    };
    const parts = new Map<THREE.Material, [THREE.BufferGeometry, THREE.Matrix4][]>([[this.gilt, []], [this.giltShade, []], [this.brass, []], [this.shade, []]]);
    for (let k = 0; k < 8; k++) {
      const cardinal = k % 2 === 0, length = k === 0 ? CHART_REACH + 10 : cardinal ? CHART_REACH : CHART_FACE + 38, width = k === 0 ? 26 : cardinal ? 24 : 15;
      const place = new THREE.Matrix4().makeTranslation(0, 0, cardinal ? 0 : -3).multiply(new THREE.Matrix4().makeRotationZ(k * Math.PI / 4));
      parts.get(k === 0 ? this.gilt : this.brass)!.push([blade(length, width, 1), place]);
      parts.get(k === 0 ? this.giltShade : this.shade)!.push([blade(length, width, -1), place.clone()]);
    }
    parts.get(this.brass)!.push([new THREE.TorusGeometry(RIM + 12, 2.2, 8, 64), new THREE.Matrix4().makeTranslation(0, 0, 1)]);
    parts.get(this.gilt)!.push([new THREE.CylinderGeometry(6, 6, 3, 16).rotateX(Math.PI / 2), new THREE.Matrix4().makeTranslation(0, 0, 2)]);
    for (const [material, pieces] of parts) this.card.add(new THREE.Mesh(keep(mergedGeometry(pieces)), material));
    this.card.position.z = -22; this.card.rotation.z = NORTH;
    this.add(this.card);
  }

  setState(state: ChartState) {
    if (canDraw() && !this.raw) {
      this.raw = Object.assign(new THREE.CanvasTexture(state.canvas), { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false });
      this.decode.material.uniforms.picture.value = this.raw;
      this.target = new THREE.WebGLRenderTarget(state.canvas.width, state.canvas.height, { depthBuffer: false, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter });
      this.target.texture.colorSpace = THREE.SRGBColorSpace; this.target.texture.anisotropy = 4;
      Object.assign(this.faceMaterial, { map: this.target.texture, emissiveMap: this.target.texture }); this.faceMaterial.needsUpdate = true;
    }
    if (this.raw && state.revision !== this.drawn) { this.drawn = state.revision; this.raw.needsUpdate = true; this.dirty = true; }
    const name = state.location.split(' · ')[0].toLocaleLowerCase().replace(/(^|\s)\S/g, letter => letter.toLocaleUpperCase());
    if (name !== this.labelText) this.drawLabel(name);
    this.expand.setIcon(state.expanded ? hudIcons.contract : hudIcons.expand); this.expand.pressed = state.expanded;
    // A pan sets the card swinging, to one side or the other with the way the view moved across the screen.
    if (this.lastFocus) {
      const dx = state.focus.x - this.lastFocus.x, dz = state.focus.z - this.lastFocus.z;
      this.spin += THREE.MathUtils.clamp((dx - dz) * .01, -.6, .6);
    }
    this.lastFocus = { ...state.focus };
  }
  /** Copies a new drawing into the face's texture, decoding it to linear so the sRGB target stores the same bytes. The HUD calls it before it draws. */
  draw(renderer: THREE.WebGLRenderer) {
    if (!this.dirty || !this.visible || !this.target) return;
    this.dirty = false;
    const previous = renderer.getRenderTarget();
    renderer.setRenderTarget(this.target); renderer.render(this.decode, this.decodeCamera); renderer.setRenderTarget(previous);
  }
  /** The decoded drawing lives in one renderer: drawn by another, it is decoded again there. */
  invalidate() { this.dirty = true; }
  /** Links the decoding pass's program, so the chart's first drawing stalls nothing. */
  warm(renderer: THREE.WebGLRenderer) {
    const target = new THREE.WebGLRenderTarget(1, 1, { depthBuffer: false }), previous = renderer.getRenderTarget();
    target.texture.colorSpace = THREE.SRGBColorSpace;
    renderer.setRenderTarget(target); renderer.render(this.decode, this.decodeCamera); renderer.setRenderTarget(previous); target.dispose();
  }
  /** Sets the card swinging, as a jolt would (unfolding or folding the chart). */
  jolt(strength = 1) { this.spin += (Math.random() < .5 ? -1 : 1) * 1.6 * strength; }

  private drawLabel(text: string) {
    this.labelText = text;
    const canvas = this.labelCanvas; if (!canvas || !this.labelTexture) return;
    const ctx = canvas.getContext('2d')!; ctx.clearRect(0, 0, canvas.width, canvas.height);
    let size = 46; ctx.font = `600 ${size}px Dosis`;
    while (ctx.measureText(text).width > canvas.width - 40 && size > 20) { size -= 2; ctx.font = `600 ${size}px Dosis`; }
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // Cut into the bronze: a dark lower edge under ivory letters.
    ctx.fillStyle = '#120c06'; ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 2);
    ctx.fillStyle = '#ead9b8'; ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    this.labelTexture.needsUpdate = true;
  }

  /** The spot under a ray on the chart's face (`u` right, `v` down, 0–1), or null. */
  pickFace(raycaster: THREE.Raycaster) {
    const uv = raycaster.intersectObject(this.face, false)[0]?.uv;
    return uv ? { u: uv.x, v: 1 - uv.y } : null;
  }
  /** The button under a ray, if any. */
  pickButton(raycaster: THREE.Raycaster): string | null {
    for (const hit of raycaster.intersectObjects([this.follow, this.expand], true)) {
      for (let object: THREE.Object3D | null = hit.object; object; object = object.parent) if (object.userData.button) return object.userData.button as string;
    }
    return null;
  }
  /** Whether a ray meets any part of the chart that should stop a click from reaching the world. */
  covers(raycaster: THREE.Raycaster) { return raycaster.intersectObjects([this.face, this.plaque, ...this.children.filter(child => child !== this.card)], true).some(hit => hit.object instanceof THREE.Mesh); }

  update(dt: number) {
    this.time += dt;
    // A damped spring pulls north back to the upper right; a faint tremor keeps the card alive at rest.
    this.spin += (-28 * this.swing - 2.6 * this.spin) * dt;
    this.swing = THREE.MathUtils.clamp(this.swing + this.spin * dt, -1.2, 1.2);
    this.card.rotation.z = NORTH + this.swing + Math.sin(this.time * 7.3) * .006 + Math.sin(this.time * 12.9) * .004;
    this.follow.update(dt, this.time); this.expand.update(dt, this.time);
  }

  dispose() {
    this.follow.dispose(); this.expand.dispose(); if (this.ownsKit) this.kit.dispose(); this.raw?.dispose(); this.target?.dispose(); this.labelTexture?.dispose();
    this.decode.geometry.dispose(); this.decode.material.dispose();
    for (const item of this.disposables) item.dispose();
  }
}

/** A chamfered bronze bar, `width` by `height`, its ends cut to points like the dock's rail. */
export function plaqueGeometry(width: number, height: number) {
  const r = height / 2, shape = new THREE.Shape();
  shape.moveTo(-width / 2 + r, -r); shape.lineTo(width / 2 - r, -r); shape.lineTo(width / 2, 0); shape.lineTo(width / 2 - r, r);
  shape.lineTo(-width / 2 + r, r); shape.lineTo(-width / 2, 0); shape.closePath();
  return new THREE.ExtrudeGeometry(shape, { depth: 4, bevelEnabled: true, bevelThickness: 2, bevelSize: 2, bevelSegments: 1 });
}

/** A full-frame pass that writes a raw sRGB drawing out as linear colour, for an sRGB target to store again. */
function decodeMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: { picture: { value: null } },
    vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
    fragmentShader: 'uniform sampler2D picture; varying vec2 vUv; void main() { gl_FragColor = sRGBTransferEOTF(texture2D(picture, vUv)); }',
    depthTest: false, depthWrite: false,
  });
}
